import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  ArrowsClockwise, CircleNotch, DownloadSimple, MagnifyingGlass, Buildings, Factory, Receipt, X,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const money = (value) => {
  if (value === null || value === undefined) return "—";
  const sign = Number(value) < 0 ? "-" : "";
  return `${sign}₹${Math.abs(Number(value)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const lakh = (value) => {
  if (value === null || value === undefined) return "—";
  const n = Math.abs(Number(value));
  const sign = Number(value) < 0 ? "-" : "";
  if (n >= 1e7) return `${sign}₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${sign}₹${(n / 1e5).toFixed(2)} L`;
  return money(value);
};
const qty = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const Stat = ({ label, value, hint, testId, tone = "slate" }) => (
  <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-[#667085] font-bold">{label}</p>
    <p className={`text-xl font-heading mt-1 tabular-nums ${tone === "teal" ? "text-[#0E7C86]" : tone === "red" ? "text-[#B42318]" : "text-[#101828]"}`}>
      {value}
    </p>
    {hint ? <p className="text-[11px] text-[#98A2B3] mt-0.5">{hint}</p> : null}
  </div>
);

/** Financial Audit (Oct 28 2026) - every figure is SAP's own, read from the
    user's journal report and matched to a confirmation by source document id. */
export const FinancialAuditView = () => {
  const today = new Date();
  const [filters, setFilters] = useState({
    date_from: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`,
    date_to: isoDay(today),
    site_id: "all",
    cost_center: "all",
    search: "",
  });
  const [options, setOptions] = useState({ sites: [], cost_centers: [], last_sync: null, line_count: 0 });
  const [plants, setPlants] = useState(null);
  const [departments, setDepartments] = useState(null);
  const [lines, setLines] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const params = useMemo(() => ({
    date_from: filters.date_from, date_to: filters.date_to,
    ...(filters.site_id !== "all" ? { site_id: filters.site_id } : {}),
    ...(filters.cost_center !== "all" ? { cost_center: filters.cost_center } : {}),
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
  }), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d, l, o] = await Promise.all([
        axios.get(`${API}/financial-audit/plants`, { params: { date_from: params.date_from, date_to: params.date_to, ...(params.site_id ? { site_id: params.site_id } : {}) } }),
        axios.get(`${API}/financial-audit/departments`, { params }),
        axios.get(`${API}/financial-audit/confirmations`, { params: { ...params, limit: 500 } }),
        axios.get(`${API}/financial-audit/filters`),
      ]);
      setPlants(p.data); setDepartments(d.data); setLines(l.data); setOptions(o.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the financial figures");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { const t = setTimeout(load, filters.search ? 400 : 0); return () => clearTimeout(t); }, [load, filters.search]);

  const refresh = async (window) => {
    setBusy(window ? "load" : "refresh");
    try {
      const body = window ? { date_from: window.from, date_to: window.to } : { days_back: 1 };
      const { data } = await axios.post(`${API}/financial-audit/refresh`, body, { timeout: 280000 });
      if (data.error) toast.error(`SAP: ${data.error}`);
      else toast.success(`SAP read: ${data.window?.lines || 0} journal line(s) over ${data.window?.days || 0} day(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAP did not answer - the hourly refresh will try again");
    } finally { setBusy(""); }
  };

  const download = async () => {
    setBusy("export");
    try {
      const { data } = await axios.get(`${API}/financial-audit/export`, { params });
      if (!data.rows.length) { toast.error("Nothing to export for these dates."); return; }
      const sheet = XLSX.utils.aoa_to_sheet([data.columns, ...data.rows]);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Financial Audit");
      XLSX.writeFile(book, `Financial_Audit_${params.date_from}_to_${params.date_to}.xlsx`);
      toast.success(`${data.rows.length} line(s) exported`);
    } catch (e) {
      toast.error("Export failed.");
    } finally { setBusy(""); }
  };

  const totals = lines?.totals || {};
  const deptTotals = departments?.totals || {};

  return (
    <div className="space-y-4" data-testid="financial-audit-view">
      <div className="bg-white border border-[#E4E7EC] rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">From</Label>
            <Input type="date" value={filters.date_from} className="h-9 w-[150px]"
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="fa-date-from" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">To</Label>
            <Input type="date" value={filters.date_to} className="h-9 w-[150px]"
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="fa-date-to" />
          </div>
          <div className="w-32">
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Plant</Label>
            <Select value={filters.site_id} onValueChange={(v) => setFilters((f) => ({ ...f, site_id: v }))}>
              <SelectTrigger className="bg-white h-9" data-testid="fa-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plants</SelectItem>
                {(options.sites || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Department</Label>
            <Select value={filters.cost_center} onValueChange={(v) => setFilters((f) => ({ ...f, cost_center: v }))}>
              <SelectTrigger className="bg-white h-9" data-testid="fa-cost-center"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All departments</SelectItem>
                {(options.cost_centers || []).map((c) => (
                  <SelectItem key={c.cost_center_id} value={c.cost_center_id}>
                    {c.cost_center_id} · {c.cost_center_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
              <Input value={filters.search} className="pl-8 pr-8 h-9" placeholder="GAC id, item or accounting doc"
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="fa-search" />
              {filters.search ? (
                <button type="button" onClick={() => setFilters((f) => ({ ...f, search: "" }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#98A2B3] hover:text-[#344054] transition-colors"
                  aria-label="Clear search" data-testid="fa-search-clear"><X size={14} /></button>
              ) : null}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => refresh()} disabled={!!busy} data-testid="fa-refresh">
            {busy === "refresh" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />}
            Refresh from SAP
          </Button>
          <Button size="sm" onClick={download} disabled={!!busy} className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="fa-export">
            {busy === "export" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <DownloadSimple size={14} className="mr-1.5" />}
            Download
          </Button>
        </div>
        <p className="text-[11px] text-[#667085] mt-3">
          Values come straight from SAP's journal entries for RAY INTERNATIONAL and RADISH TECHNOLOGIES, matched to each
          confirmation by its source document id. Refreshed automatically every hour
          {options.last_sync ? ` · last read ${new Date(options.last_sync).toLocaleString("en-IN")}` : ""}
          {options.line_count ? ` · ${Number(options.line_count).toLocaleString("en-IN")} journal lines held` : ""}.
        </p>
      </div>

      {plants?.coverage?.missing_count ? (
        <div className="flex flex-wrap items-center gap-3 bg-[#FFFAEB] border border-[#FEDF89] rounded-xl px-4 py-3"
          data-testid="fa-coverage-warning">
          <p className="text-xs text-[#B54708] flex-1 min-w-[260px]">
            <strong>{plants.coverage.missing_count} of these {plants.coverage.days} day(s) have not been read from SAP yet</strong> -
            the figures below cover {plants.coverage.loaded} day(s). Load them to see the whole period.
          </p>
          <Button size="sm" variant="outline" className="h-8 text-[11px] border-[#FEDF89]"
            onClick={() => refresh({ from: filters.date_from, to: filters.date_to })} disabled={!!busy}
            data-testid="fa-load-missing-days">
            {busy === "load" ? <CircleNotch size={12} className="animate-spin mr-1" /> : <ArrowsClockwise size={12} className="mr-1" />}
            Load {plants.coverage.missing_count} day(s) from SAP
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Stock on hand, all plants" value={lakh(plants?.total_stock_value)} tone="teal"
          hint={plants?.stock_as_of ? `live stock ${new Date(plants.stock_as_of).toLocaleString("en-IN")}` : ""}
          testId="fa-stat-stock" />
        <Stat label="Booked on inventory account" value={lakh(plants?.total_booked_net)}
          hint="net of issues and receipts in this period" testId="fa-stat-booked" />
        <Stat label="Consumed by departments" value={lakh(deptTotals.amount)} tone="red"
          hint={`${deptTotals.departments || 0} department(s) over ${deptTotals.days || 0} day(s)`} testId="fa-stat-dept" />
        <Stat label="Confirmations valued" value={(totals.confirmations || 0).toLocaleString("en-IN")}
          hint={`${totals.lines || 0} line(s) · ${totals.outside_app || 0} posted outside this app`} testId="fa-stat-confirmations" />
      </div>

      <Tabs defaultValue="plants">
        <TabsList className="flex-wrap h-auto" data-testid="fa-tabs">
          <TabsTrigger value="plants" data-testid="fa-tab-plants"><Factory size={14} className="mr-1.5 hidden sm:block" /> Plant Inventory</TabsTrigger>
          <TabsTrigger value="departments" data-testid="fa-tab-departments"><Buildings size={14} className="mr-1.5 hidden sm:block" /> Department Postings</TabsTrigger>
          <TabsTrigger value="confirmations" data-testid="fa-tab-confirmations"><Receipt size={14} className="mr-1.5 hidden sm:block" /> Consumed Items</TabsTrigger>
        </TabsList>

        <TabsContent value="plants">
          <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
            <div className="overflow-auto max-h-[60vh]">
              <Table className="min-w-[900px]">
                <TableHeader className="sticky top-0 bg-[#F9FAFB] z-10">
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">Plant</TableHead>
                    <TableHead className="text-xs">Company</TableHead>
                    <TableHead className="text-xs text-right">Items</TableHead>
                    <TableHead className="text-xs text-right">Qty on hand</TableHead>
                    <TableHead className="text-xs text-right">Stock value (SAP)</TableHead>
                    <TableHead className="text-xs text-right">Issued in period</TableHead>
                    <TableHead className="text-xs text-right">Received in period</TableHead>
                    <TableHead className="text-xs text-right">Net booked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && !plants && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-[#667085]">Loading...</TableCell></TableRow>}
                  {plants?.rows?.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-[#667085]" data-testid="fa-plants-empty">No plant figures yet - press Refresh from SAP.</TableCell></TableRow>}
                  {(plants?.rows || []).map((r, i) => (
                    <TableRow key={r.site_id} data-testid={`fa-plant-row-${r.site_id}`}>
                      <TableCell className="text-xs font-bold text-[#101828]">{r.site_id}
                        {r.site_name ? <span className="block text-[10px] font-normal text-[#98A2B3]">{r.site_name}</span> : null}</TableCell>
                      <TableCell className="text-xs text-[#475467]">{r.company_id || "—"}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{(r.item_count || 0).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{qty(r.quantity)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-bold text-[#0E7C86]" data-testid={`fa-plant-value-${r.site_id}`}>
                        {r.stock_value === null ? "—" : money(r.stock_value)}
                        {r.unpriced_items ? <span className="block text-[10px] font-normal text-[#B54708]">{r.unpriced_items} item(s) unpriced</span> : null}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-[#B42318]">{money(-r.booked_issued)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-[#027A48]">{money(r.booked_received)}</TableCell>
                      <TableCell className={`text-xs text-right tabular-nums font-bold ${r.booked_net < 0 ? "text-[#B42318]" : "text-[#027A48]"}`}>
                        {money(r.booked_net)}
                        <span className="block text-[10px] font-normal text-[#98A2B3]">{r.booked_lines} line(s)</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="px-4 py-2 border-t border-[#E4E7EC] text-[11px] text-[#667085]">
              Stock value is the live SAP stock at its SAP unit cost{filters.site_id !== "all" ? ` for ${filters.site_id}` : ""}.
              Net booked is what SAP posted on the inventory accounts between {plants?.from} and {plants?.to} - a
              movement, not a balance.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="departments">
          <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
            <div className="overflow-auto max-h-[60vh]">
              <Table className="min-w-[900px]">
                <TableHeader className="sticky top-0 bg-[#F9FAFB] z-10">
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">Posting date</TableHead>
                    <TableHead className="text-xs">Department</TableHead>
                    <TableHead className="text-xs">Plant</TableHead>
                    <TableHead className="text-xs text-right">Quantity</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                    <TableHead className="text-xs text-right">Items</TableHead>
                    <TableHead className="text-xs text-right">Confirmations</TableHead>
                    <TableHead className="text-xs text-right">Return lines</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && !departments && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-[#667085]">Loading...</TableCell></TableRow>}
                  {departments?.rows?.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-[#667085]" data-testid="fa-departments-empty">Nothing posted by any department in these dates.</TableCell></TableRow>}
                  {(departments?.rows || []).map((r, i) => (
                    <TableRow key={`${r.posting_date}-${r.cost_center_id}`} data-testid={`fa-dept-row-${i}`}>
                      <TableCell className="text-xs text-[#344054] whitespace-nowrap">{r.posting_date}</TableCell>
                      <TableCell className="text-xs text-[#101828] font-bold">{r.cost_center_id}
                        <span className="block text-[10px] font-normal text-[#98A2B3]">{r.cost_center_name}</span></TableCell>
                      <TableCell className="text-xs text-[#475467]">{r.site_id} <span className="text-[#98A2B3]">{r.company_id}</span></TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{qty(r.quantity)}</TableCell>
                      <TableCell className={`text-xs text-right tabular-nums font-bold ${r.amount < 0 ? "text-[#027A48]" : "text-[#101828]"}`}
                        data-testid={`fa-dept-amount-${i}`}>{money(r.amount)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-[#475467]">{r.item_count}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-[#475467]">{r.confirmation_count}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">
                        {r.return_lines ? <Badge variant="outline" className="text-[10px] bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]">{r.return_lines}</Badge> : <span className="text-[#98A2B3]">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="px-4 py-2 border-t border-[#E4E7EC] text-[11px] text-[#667085]">
              One row per department per posting date. A negative amount means that day's returns outweighed what the
              department consumed.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="confirmations">
          <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
            <div className="overflow-auto max-h-[60vh]">
              <Table className="min-w-[1200px]">
                <TableHeader className="sticky top-0 bg-[#F9FAFB] z-10">
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">GAC ID</TableHead>
                    <TableHead className="text-xs">Posted</TableHead>
                    <TableHead className="text-xs">Plant</TableHead>
                    <TableHead className="text-xs">Department</TableHead>
                    <TableHead className="text-xs">Item</TableHead>
                    <TableHead className="text-xs">Item name</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                    <TableHead className="text-xs">Transaction</TableHead>
                    <TableHead className="text-xs">Accounting doc</TableHead>
                    <TableHead className="text-xs">Submitted by</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && !lines && <TableRow><TableCell colSpan={11} className="py-8 text-center text-sm text-[#667085]">Loading...</TableCell></TableRow>}
                  {lines?.rows?.length === 0 && <TableRow><TableCell colSpan={11} className="py-8 text-center text-sm text-[#667085]" data-testid="fa-confirmations-empty">No consumed-item confirmations in these dates.</TableCell></TableRow>}
                  {(lines?.rows || []).map((r, i) => (
                    <TableRow key={r.key} data-testid={`fa-line-row-${i}`}>
                      <TableCell className="text-xs font-mono font-bold text-[#101828]" data-testid={`fa-line-gac-${i}`}>{r.gac_id}</TableCell>
                      <TableCell className="text-xs text-[#475467] whitespace-nowrap">{r.posting_date}</TableCell>
                      <TableCell className="text-xs text-[#475467]">{r.site_id}</TableCell>
                      <TableCell className="text-xs text-[#475467]">{r.cost_center_id || "—"}
                        {r.cost_center_name ? <span className="block text-[10px] text-[#98A2B3]">{r.cost_center_name}</span> : null}</TableCell>
                      <TableCell className="text-xs font-mono text-[#101828]">{r.material_id}</TableCell>
                      <TableCell className="text-xs text-[#475467] max-w-[200px] truncate" title={r.material_description}>{r.material_description || "—"}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{qty(r.quantity)} <span className="text-[#98A2B3]">{r.uom}</span></TableCell>
                      <TableCell className={`text-xs text-right tabular-nums font-bold ${r.is_return ? "text-[#027A48]" : "text-[#101828]"}`}
                        data-testid={`fa-line-amount-${i}`}>{money(r.amount_abs)}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className={`text-[10px] ${r.is_return ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#F9FAFB] text-[#475467] border-[#E4E7EC]"}`}>
                          {r.is_return ? "Return" : "Consumption"}
                        </Badge>
                        {r.reversal ? <Badge variant="outline" className="ml-1 text-[10px] bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Reversed</Badge> : null}
                      </TableCell>
                      <TableCell className="text-[11px] font-mono text-[#667085]">{r.accounting_document}<span className="text-[#98A2B3]">/{r.accounting_item}</span></TableCell>
                      <TableCell className="text-xs text-[#475467]">
                        {r.in_app ? (r.submitted_by || "—") : <span className="text-[#98A2B3]">posted in SAP</span>}
                        {r.npd ? <span className="block text-[10px] text-[#0E7C86]">NPD {r.project_id}</span> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="px-4 py-2 border-t border-[#E4E7EC] text-[11px] text-[#667085]" data-testid="fa-confirmations-footer">
              {totals.lines || 0} line(s) · consumed {money(totals.consumed_amount)} · returned {money(totals.returned_amount)} ·
              net {money(totals.net_amount)}. Each row is one accounting item behind a Goods and Activity Confirmation.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
